import React, { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import LazyLoad from 'react-lazyload';
import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent from '../../../hooks/usePageContent';

// Components
import Hero from '../content/Hero';
import CategoryList from '../content/CategoryList';
import PodList from '../content/PodList';
import NeighborhoodList from '../content/NeighborhoodList';
import CityList from '../content/CityList';
import LogoList from '../content/LogoList';
import Triple from '../content/Triple';
import Meta from '../../shared/Meta';
import CtaImage from '../content/CtaImage';
import FeaturedTrucks from '../FeaturedTrucks';
import CuisineLink from '../content/CuisineLink';
import CateringCta from '../content/CateringCta';
import AnyEvent from '../content/AnyEvent';
import FeaturedBlogPostsScroll from '../blog/FeaturedBlogPostsScroll';
import SimpleCta from '../content/SimpleCta';

// Misc
import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import tripleStyles from '../content/Triple.module.postcss';
import cuisineLinkStyles from '../content/CuisineLink.module.postcss';
import CDN_BASE from '../../../utils/cdn';

const featuredTrucks = { featured: true };

// Homepage.
//
// Structure (owner decision, 4 Sep 2026 — see design/voice-bible-2026-09-v4.2.md
// §19): hero with one CTA → trusted-by logos → property services triple →
// featured trucks → office / wedding / events catering blocks → event types →
// cuisine grid → vendor CTA → blog → closing catering CTA → discovery lists.
// The voice bible governs the words in each block, not which blocks exist or
// their order. Do not reorder or remove sections from a copy document.
//
// Copy: CMS row `home` (schemas/home.js mirrors these keys). Every reference
// falls back to the JSX below when no published row exists for the city.
function Home() {
  const { city_name, vehicle_type, site_title } = useContext(ConfigContext);
  const vehicleType = useMemo(() => vehicle_type.toLowerCase(), [vehicle_type]);
  const citySlug = city_name.toLowerCase();
  const isPortland = useMemo(() => city_name === 'Portland', [city_name]);

  const { content: cms } = usePageContent('home');

  const structuredData = useMemo(() => ({ home: true }), []);

  let heroImg = `${CDN_BASE}/photos/home-hero.webp`;
  if (isPortland) {
    heroImg = `${CDN_BASE}/photos/portland-home.webp`;
  }

  return (
    <div>
      <Meta
        title={`Food ${vehicleType}s near me in ${city_name}`}
        structuredData={structuredData}
        description={`Find food ${vehicleType}s near you in ${city_name}. ${site_title} makes it easy to discover, book, and cater food ${vehicleType}s for corporate events, weddings, office lunches, and festivals. Browse menus, compare quotes, and book today.`}
        canonical='/'
      />
      <Hero photo={heroImg}>
        <h1>{cms?.hero?.headline || `Find and book ${city_name}'s food ${vehicleType}s`}</h1>
        <p>{cms?.hero?.subheadline || `See who's cooking nearby, try something new, or book a ${vehicleType} for your next event.`}</p>
        <Link to='/explore' className='Button Button--large'>{cms?.hero?.cta_label || `Food ${vehicleType}s near me`}</Link>
      </Hero>

      <div className='Section'>
        <LogoList title={`Companies that book food ${vehicleType}s with us`} titleClassName='u-textCenter' />
      </div>

      <section className='Section' aria-label='Services'>
        <h2>{cms?.services?.heading || `Bring ${city_name}'s food ${vehicleType}s to you`}</h2>
        <div className={cn(tripleStyles.Triple_wrapper, tripleStyles.Triple_wrapper__tall)}>
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-office-food-truck-lunch.webp`}
            isResponsive
            alt='Food truck serving lunch at an office park'
            title='Office parks and campuses'
            text={cms?.services?.office_text || `A rotating lineup for your office or campus. We build the schedule and coordinate with the ${vehicleType}s.`}
            link='/services/office-parks'
          />
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-apartment-food-truck-schedule.webp`}
            isResponsive
            alt='Food truck visiting an apartment community'
            title='Apartments and neighborhoods'
            text={cms?.services?.residential_text || `Weekly food ${vehicleType}s for your apartment community, with a schedule residents can follow.`}
            link='/services/residential'
          />
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-brewery-food-truck.webp`}
            isResponsive
            alt='Food truck parked outside a brewery'
            title='Breweries and taprooms'
            text={cms?.services?.breweries_text || `Food ${vehicleType}s for your taproom, so customers can eat where they drink.`}
            link='/services/breweries'
          />
        </div>
      </section>

      {/*
        FeaturedTrucks fires `useGetTrucksQuery` on mount, which PSI flagged as
        part of the 5.8s critical-path API blocker on mobile. A larger `offset`
        (400px vs the 300px used for the below-fold block) makes desktop users
        — where FeaturedTrucks is closer to the fold — still get it mounted
        almost immediately, while mobile (where it's fully below the fold on
        a 360×640 viewport) defers the fetch until scroll.
      */}
      <LazyLoad height={600} offset={400} once>
        <FeaturedTrucks
          title={`Featured food ${vehicleType}s`}
          options={featuredTrucks}
        />
      </LazyLoad>

      <section className='Section--extraPadding' aria-label='Catering'>
        <h2 className='u-textCenter'>Food {vehicleType} catering for any event</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-corporate-food-truck-catering.webp`} alt='Food truck catering at a corporate office' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>{cms?.office_catering?.heading || 'Office catering for your team'}</h3>

            <p>{cms?.office_catering?.body || `Bring a food ${vehicleType} to your office for a team lunch, an appreciation day, or a company party. ${city_name} food ${vehicleType}s cook to order and can set up outside or serve indoors. Tell us what you're planning and we'll find ${vehicleType}s that fit.`}</p>

            <div>
              <Link
                to='/food-truck-catering/corporate-catering'
                className='Button'
              >
                See office catering
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-wedding-food-truck.webp`} alt='Food truck serving at a wedding reception' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>{cms?.wedding_catering?.heading || `Wedding catering, food ${vehicleType} style`}</h3>

            <p>{cms?.wedding_catering?.body || `Plated dinners, taco bars, BBQ buffets, or late-night snacks. ${city_name}'s wedding food ${vehicleType}s can build a menu around your day and your guests' dietary needs. Tell us the venue and guest count and we'll find ${vehicleType}s that fit.`}</p>

            <div>
              <Link
                to='/food-truck-catering/wedding-catering'
                className='Button'
              >
                See wedding catering
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        {/* Every city gets the events section. Portland used to swap in a
            legacy "About FoodCartsPortland.com" block from the old site. */}
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-community-food-truck-event.webp`} alt='Food trucks at a community event' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>{cms?.events?.heading || 'Events and festivals'}</h3>
            <p>{cms?.events?.body || `Block parties, fundraisers, school events, grand openings, and festivals. Whether you need one ${vehicleType} or a full lineup, compare quotes, browse menus, and book in one place.`}</p>
            <div>
              <Link
                to='/food-truck-catering/community-events'
                className='Button'
              >
                See event catering
              </Link>
            </div>
          </div>
        </div>
      </section>

      <AnyEvent />

      <section className='Section--extraPadding' aria-label='Popular cuisines'>
        <h3 className='u-phosphate'>{cms?.browse?.heading || 'What are you hungry for?'}</h3>
        <div className={cuisineLinkStyles.CuisineLinkContainer}>
          <CuisineLink title='Tacos' emoji='&#127790;' link='/taco-trucks' />
          <CuisineLink title='Sandwiches' emoji='&#129386;' link='/sandwich-food-trucks' />
          <CuisineLink title='Desserts' emoji='&#127846;' link='/dessert-food-trucks' />
          <CuisineLink title='Burgers' emoji='&#127828;' link='/burger-food-trucks' />
          <CuisineLink title='BBQ' emoji='&#127830;' link='/bbq-food-trucks' />
          <CuisineLink title='Breakfast' emoji='&#129374;' link='/breakfast-food-trucks' />
          <CuisineLink title='Pizza' emoji='&#127829;' link='/pizza-food-trucks' />
          <CuisineLink title='Healthy' emoji='&#129367;' link='/healthy-food-trucks' />
          <CuisineLink title='Gyros' emoji='&#129369;' link='/gyro-food-trucks' />
          <CuisineLink title='Asian' emoji='&#129377;' link='/asian-food-trucks' />
        </div>
      </section>

      <section className='Section--extraPadding' aria-label='Vendors'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-book-a-food-truck.webp`} alt='Food truck vendor at the service window' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>{cms?.vendor_cta?.heading || `Own a food ${vehicleType}?`}</h3>

            <p>{cms?.vendor_cta?.body || 'Build your public profile, receive catering requests, and apply for local spots.'}</p>

            <div>
              <Link
                to='/vendors'
                className='Button'
              >
                {cms?.vendor_cta?.cta_label || 'Join as a vendor'}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Below-the-fold: defer mounting (and thus the RTK Query fetches) until
          the user scrolls close to them. Each list component fires its own
          `useGetXQuery` on mount, so wrapping the JSX is sufficient — no slice
          changes needed. `react-lazyload` is the same pattern used in
          Location.jsx, Truck.jsx, BlogPost.jsx. */}
      <LazyLoad height={400} offset={300} once>
        <section className='Section'>
          <FeaturedBlogPostsScroll title='From the blog' />
        </section>
      </LazyLoad>

      <LazyLoad height={120} offset={300} once>
        <section className='Section u-mb2'>
          <SimpleCta
            text={`Food ${vehicleType} news, event tips, and what's opening around ${city_name}.`}
            link='/blog'
            linkTitle='Read the blog'
          />
        </section>
      </LazyLoad>

      <LazyLoad height={300} offset={300} once>
        <section className='Section'>
          <CateringCta />
        </section>
      </LazyLoad>

      <hr className='u-mt2' />

      <LazyLoad height={200} offset={300} once>
        <section className='Section'>
          <NeighborhoodList vehicleType={vehicleType} />
        </section>
      </LazyLoad>

      <LazyLoad height={200} offset={300} once>
        <section className='Section'>
          <CityList cityName={city_name} vehicleType={vehicleType} />
        </section>
      </LazyLoad>

      <LazyLoad height={200} offset={300} once>
        <section className='Section'>
          <CategoryList vehicleType={vehicleType} />
        </section>
      </LazyLoad>

      <LazyLoad height={200} offset={300} once>
        <section className='Section'>
          <PodList cityName={city_name} vehicleType={vehicleType} />
        </section>
      </LazyLoad>
    </div>
  );
}

export default Home;
